import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismTooltipFooter,
  PrismTooltipHeader,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightDifferenceRow from 'lumen/components/hotel-and-market/revenue-insight/tables/rows/tooltip-revenue-insight-difference-row';
import HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow from 'lumen/components/hotel-and-market/revenue-insight/tables/rows/tooltip-revenue-insight-row';
import type { RevenueInsightSegmentationValue } from 'lumen/models';
import {
  type CalculatedProperty,
  INVALID_RESERVATIONS_MESSAGE,
  type KpiCategory,
  type KPIS,
  REVENUE_INSIGHT_METRICS,
  RI_SECTIONS,
} from 'lumen/utils/constants/ri-constant-values';
import roundSegmentationValue from 'lumen/utils/revenue-insight/round-segmentation-value';
import { segmentationValueFormatter } from 'lumen/utils/revenue-insight/segmentation-value-formatter';

type RevenueInsightMetric =
  (typeof REVENUE_INSIGHT_METRICS)[keyof typeof REVENUE_INSIGHT_METRICS];

type SupportedKpi =
  | typeof KPIS.NR_OF_ROOMS
  | typeof KPIS.REVENUE
  | typeof KPIS.ADR;

interface HotelAndMarketRevenueInsightTooltipsTooltipPaceSignature {
  Args: {
    allSegmentation?: [RevenueInsightSegmentationValue];
    data: RevenueInsightSegmentationValue;
    isPickupLastYearVisible?: boolean;
    kpiCategory?: KpiCategory;
    kpis: SupportedKpi[];
    metricKey?: SupportedKpi;
  };
}

export default class HotelAndMarketRevenueInsightTooltipsTooltipPace extends Component<HotelAndMarketRevenueInsightTooltipsTooltipPaceSignature> {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];

  get hasForecastReference(): boolean {
    const { data, metricKey } = this.args;

    if (!metricKey) {
      return false;
    }

    const forecastReferenceValue =
      data.calculatedValues?.forecastReference[metricKey];

    return (
      forecastReferenceValue !== null && forecastReferenceValue !== undefined
    );
  }

  get isPickupAndPaceIncomplete(): boolean {
    const metricsToCheck = [...this.thisYearMetrics, ...this.lastYearMetrics];

    return metricsToCheck.some((metric) => {
      return (
        // @ts-expect-error: Incorrect type
        this.tooltipValues[metric].message === INVALID_RESERVATIONS_MESSAGE
      );
    });
  }

  get kpiTitles(): string[] {
    const { kpis } = this.args;

    return kpis.map((kpi) => this.intl.t(`revenue_insight.${kpi}`));
  }

  get lastYearMetrics(): RevenueInsightMetric[] {
    const metrics: RevenueInsightMetric[] = [REVENUE_INSIGHT_METRICS.REFERENCE];

    if (
      this.access.hasFeature('revenueinsight.cancellations') &&
      !this.showPickupLYinThisYear &&
      !this.riSelections.isTargetActive &&
      this.riSelections.isSingleProperty
    ) {
      metrics.push(REVENUE_INSIGHT_METRICS.CANCELLATION_REFERENCE_OR_FILTERED);
    }

    return metrics;
  }

  get lastYearValueMetrics(): RevenueInsightMetric[] {
    const metrics: RevenueInsightMetric[] = [...this.lastYearMetrics];

    if (this.showGrossValues) {
      metrics[0] = REVENUE_INSIGHT_METRICS.GROSS_REFERENCE;
    }

    return metrics;
  }

  get showGrossValues(): boolean {
    const { data } = this.args;
    const { isCancellationsOnlyView, isTargetActive, isSingleProperty } =
      this.riSelections;

    const isPickup = data.segmentationId === RI_BREAKDOWNS.PICKUP;

    return (
      isSingleProperty &&
      (isCancellationsOnlyView || (isPickup && !isTargetActive))
    );
  }

  get showPickupLY(): boolean {
    const { isPickupLastYearVisible } = this.args;

    return (
      this.riSelections.showPickupLY &&
      Boolean(isPickupLastYearVisible) &&
      !this.riSelections.isCancellationsOnlyView &&
      this.hasForecastReference
    );
  }

  get showPickupLYinThisYear(): boolean {
    const { data } = this.args;

    return this.showPickupLY && data.segmentationId === RI_BREAKDOWNS.PACE;
  }

  get targetValue(): CalculatedProperty[] {
    if (this.riSelections.isBudgetActive) {
      return ['budget'];
    }

    if (this.riSelections.isForecastActive) {
      return ['forecast'];
    }

    return [];
  }

  get thisYearMetrics(): RevenueInsightMetric[] {
    if (this.showPickupLYinThisYear) {
      return [REVENUE_INSIGHT_METRICS.FORECAST_REFERENCE];
    }

    if (
      this.access.hasFeature('revenueinsight.cancellations') &&
      !this.riSelections.isTargetActive &&
      this.riSelections.isSingleProperty
    ) {
      return [
        REVENUE_INSIGHT_METRICS.BOB,
        REVENUE_INSIGHT_METRICS.CANCELLATION_BOB_OR_FILTERED,
      ];
    }

    return [REVENUE_INSIGHT_METRICS.BOB];
  }

  get thisYearValueMetrics(): RevenueInsightMetric[] {
    const metrics: RevenueInsightMetric[] = [...this.thisYearMetrics];

    if (this.showGrossValues && metrics[0] === REVENUE_INSIGHT_METRICS.BOB) {
      metrics[0] = REVENUE_INSIGHT_METRICS.GROSS_BOB;
    }

    return metrics;
  }

  get tooltipTitle(): string {
    const { data } = this.args;

    if (data.displayValue) {
      return data.displayValue;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return segmentationValueFormatter(
      data.segmentationId,
      data.segmentationValue,
      {
        intl: this.intl,
      },
    );
  }

  get tooltipValues(): Record<string, unknown> | null {
    const { allSegmentation, data, kpiCategory } = this.args;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const copyOfData = data.copy() as RevenueInsightSegmentationValue;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    copyOfData.calculatedValues = data.calculatedValues.calculateValues();

    if (allSegmentation) {
      const { calculatedValues } = allSegmentation[0];

      copyOfData.calculatedValues.budget = calculatedValues.budget;
      copyOfData.calculatedValues.forecast = calculatedValues.forecast;
    }

    // @ts-expect-error: Incorrect type
    return roundSegmentationValue(copyOfData, false, kpiCategory);
  }

  <template>
    <PrismTooltipHeader data-test-id="tooltip-pace-header">
      {{this.tooltipTitle}}
    </PrismTooltipHeader>

    <table class="tooltip-revenue-insight">
      <thead>
        <tr>
          <th></th>
          <th></th>
          {{#each this.kpiTitles as |kpiTitle|}}
            <th class="text-200--bold">{{kpiTitle}}</th>
          {{/each}}
        </tr>
      </thead>
      <tbody>
        <tr>
          <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow
            {{! @glint-expect-error: Incorrect type }}
            @kpiCategory={{@kpiCategory}}
            @kpis={{@kpis}}
            {{! @glint-expect-error: Incorrect type }}
            @metrics={{this.thisYearMetrics}}
            @showPickupLY={{this.showPickupLYinThisYear}}
            {{! @glint-expect-error: Incorrect type }}
            @tooltipValues={{this.tooltipValues}}
            {{! @glint-expect-error: Incorrect type }}
            @valueMetrics={{this.thisYearValueMetrics}}
          />
        </tr>
        <tr>
          <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow
            {{! @glint-expect-error: Incorrect type }}
            @kpiCategory={{@kpiCategory}}
            @kpis={{@kpis}}
            {{! @glint-expect-error: Incorrect type }}
            @metrics={{this.lastYearMetrics}}
            @section={{RI_SECTIONS.PICKUP}}
            {{! @glint-expect-error: Incorrect type }}
            @tooltipValues={{this.tooltipValues}}
            {{! @glint-expect-error: Incorrect type }}
            @valueMetrics={{this.lastYearValueMetrics}}
          />
        </tr>
        {{#if this.riSelections.isBookingsOnlyView}}
          <tr>
            <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightDifferenceRow
              @kpis={{@kpis}}
              @metrics={{this.thisYearMetrics}}
              {{! @glint-expect-error: Incorrect type }}
              @showPickupLY={{this.showPickupLYinThisYear}}
              @tooltipValues={{this.tooltipValues}}
            />
          </tr>
        {{/if}}
        {{#if this.riSelections.isTargetActive}}
          <tr>
            <HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow
              {{! @glint-expect-error: Incorrect type }}
              @kpiCategory={{@kpiCategory}}
              @kpis={{@kpis}}
              @metrics={{this.targetValue}}
              {{! @glint-expect-error: Incorrect type }}
              @tooltipValues={{this.tooltipValues}}
            />
          </tr>
        {{/if}}
      </tbody>
    </table>

    {{#if this.isPickupAndPaceIncomplete}}
      <PrismTooltipFooter>
        <span class="text-200--regular">
          {{t
            "revenue_insight.pickup_and_pace.incomplete"
            integrationDate=this.riSelections.formattedIntegrationDate
          }}
        </span>
      </PrismTooltipFooter>
    {{/if}}
  </template>
}
